import { useState } from "react";
import { Search, Filter, Plus, Eye, CalendarDays, CheckCircle2, XCircle, Clock, ChevronDown, ChevronUp } from "lucide-react";
import {
  CalendarDots as PhCalendarDots,
  CheckCircle as PhCheckCircle,
  XCircle as PhXCircle,
  Clock as PhClock,
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatCard } from "@/components/StatCard";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { useBookings, useBookingsPage, useBookingStats, useCreateBooking, useUpdateBookingStatus } from "@/hooks/useBookings";
import { useHosts } from "@/hooks/useHosts";

const PLATFORM_LABELS: Record<string, string> = {
  airbnb: "Airbnb",
  vrbo: "VRBO",
  booking_com: "Booking.com",
  expedia: "Expedia",
  direct: "Direct",
  other: "Other",
};

const PURPOSE_LABELS: Record<string, string> = {
  leisure: "Leisure",
  business: "Business",
  family: "Family visit",
  relocation: "Relocation",
  other: "Other",
};

export default function Bookings() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const pageSize = 25;
  const isSearching = search.trim().length > 0;

  // New booking dialog
  const [open, setOpen] = useState(false);
  const [showOptionalGuest, setShowOptionalGuest] = useState(false);
  const [form, setForm] = useState({
    host_id: "",
    unit_id: "",
    guest_name: "",
    guest_email: "",
    guest_phone: "",
    guest_count: "1",
    check_in: "",
    check_out: "",
    fee: "0",
    // Optional / privacy-respecting
    platform: "",
    booking_reference: "",
    vehicle_info: "",
    purpose: "",
    booking_notes: "",
  });

  // View / action dialog
  const [viewBooking, setViewBooking] = useState<any>(null);
  const [blockReason, setBlockReason] = useState("");

  // While actively searching, fall back to the bounded (max 500) full-org
  // fetch and filter client-side across all of it. Otherwise, page through
  // the org's bookings 25 at a time via the server so the list scales with
  // booking history instead of loading everything up front.
  const { data: searchResults = [], isLoading: searchLoading, error: searchError } = useBookings(statusFilter, { enabled: isSearching });
  const { data: pageData, isLoading: pageLoading, error: pageError } = useBookingsPage(statusFilter, page, pageSize, { enabled: !isSearching });
  const error = isSearching ? searchError : pageError;
  const { data: stats } = useBookingStats();
  const { data: hosts = [] } = useHosts();
  const createBooking = useCreateBooking();
  const updateStatus = useUpdateBookingStatus();

  const bookings = isSearching ? searchResults : (pageData?.bookings ?? []);
  const isLoading = isSearching ? searchLoading : pageLoading;
  const totalCount = pageData?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const filtered = isSearching
    ? bookings.filter((b: any) => {
        const searchLower = search.toLowerCase();
        return (
          (b.guest_name || "").toLowerCase().includes(searchLower) ||
          (b.unit?.unit_number || "").includes(search) ||
          (b.host?.profile?.full_name || "").toLowerCase().includes(searchLower)
        );
      })
    : bookings;

  const calcNights = (checkIn: string, checkOut: string) => {
    if (!checkIn || !checkOut) return 0;
    const diff = new Date(checkOut).getTime() - new Date(checkIn).getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const handleHostChange = (hostId: string) => {
    const host = hosts.find((h: any) => h.id === hostId);
    setForm({ ...form, host_id: hostId, unit_id: host?.unit_id || "" });
  };

  const resetForm = () => {
    setForm({
      host_id: "", unit_id: "", guest_name: "", guest_email: "", guest_phone: "",
      guest_count: "1", check_in: "", check_out: "", fee: "0",
      platform: "", booking_reference: "", vehicle_info: "", purpose: "", booking_notes: "",
    });
    setShowOptionalGuest(false);
  };

  const handleSubmit = () => {
    const nights = calcNights(form.check_in, form.check_out);
    if (!form.host_id || !form.unit_id || !form.guest_name || !form.check_in || !form.check_out || nights <= 0) return;
    createBooking.mutate(
      {
        host_id: form.host_id,
        unit_id: form.unit_id,
        guest_name: form.guest_name,
        guest_email: form.guest_email || undefined,
        guest_phone: form.guest_phone || undefined,
        guest_count: parseInt(form.guest_count) || 1,
        check_in: form.check_in,
        check_out: form.check_out,
        nights,
        fee: parseFloat(form.fee) || 0,
        platform: (form.platform || undefined) as any,
        booking_reference: form.booking_reference || undefined,
        vehicle_info: form.vehicle_info || undefined,
        purpose: (form.purpose || undefined) as any,
        booking_notes: form.booking_notes || undefined,
      },
      {
        onSuccess: () => {
          setOpen(false);
          resetForm();
        },
      }
    );
  };

  const handleApprove = () => {
    if (!viewBooking) return;
    updateStatus.mutate(
      { id: viewBooking.id, status: "approved" },
      { onSuccess: () => setViewBooking(null) }
    );
  };

  const handleBlock = () => {
    if (!viewBooking || !blockReason.trim()) return;
    updateStatus.mutate(
      { id: viewBooking.id, status: "blocked", blocked_reason: blockReason.trim() },
      {
        onSuccess: () => {
          setViewBooking(null);
          setBlockReason("");
        },
      }
    );
  };

  const handleComplete = () => {
    if (!viewBooking) return;
    updateStatus.mutate(
      { id: viewBooking.id, status: "completed" },
      { onSuccess: () => setViewBooking(null) }
    );
  };

  const openViewDialog = (booking: any) => {
    setViewBooking(booking);
    setBlockReason("");
  };

  if (error) {
    console.error("[Bookings] Failed to load bookings:", error);
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center space-y-2">
          <p className="text-sm text-destructive">Failed to load bookings. Please try again.</p>
          <p className="text-xs text-muted-foreground font-mono">{(error as any)?.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Bookings</h1>
          <p className="text-sm text-muted-foreground">Manage and validate all STR bookings</p>
        </div>
        <Button size="sm" onClick={() => setOpen(true)}><Plus className="mr-1 h-4 w-4" /> New Booking</Button>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {isLoading ? (
          <>{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}</>
        ) : (
          <>
            <StatCard title="Total Bookings" value={stats?.total ?? 0} subtitle="All time" icon={PhCalendarDots} iconWeight="duotone" variant="default" />
            <StatCard title="Approved" value={stats?.approved ?? 0} icon={PhCheckCircle} iconWeight="duotone" variant="success" />
            <StatCard title="Blocked" value={stats?.blocked ?? 0} icon={PhXCircle} iconWeight="duotone" variant="destructive" />
            <StatCard title="Pending" value={stats?.pending ?? 0} icon={PhClock} iconWeight="duotone" variant="warning" />
          </>
        )}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search by guest, host, or unit..." className="pl-9 h-9" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="h-9 w-[140px] text-xs"><Filter className="mr-1 h-3 w-3" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CalendarDays className="h-10 w-10 text-muted-foreground/50 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">No bookings found</p>
              <p className="text-xs text-muted-foreground">
                {search || statusFilter !== "all" ? "Try adjusting your filters." : "No bookings have been created yet."}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Unit</TableHead>
                  <TableHead className="text-xs">Host</TableHead>
                  <TableHead className="text-xs">Guest</TableHead>
                  <TableHead className="text-xs">Guests</TableHead>
                  <TableHead className="text-xs">Check-in</TableHead>
                  <TableHead className="text-xs">Check-out</TableHead>
                  <TableHead className="text-xs">Nights</TableHead>
                  <TableHead className="text-xs">Platform</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Fee</TableHead>
                  <TableHead className="text-xs w-[60px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((b: any) => (
                  <TableRow key={b.id}>
                    <TableCell className="font-medium text-sm">{b.unit?.unit_number || "—"}</TableCell>
                    <TableCell className="text-xs">{b.host?.profile?.full_name || "—"}</TableCell>
                    <TableCell className="text-sm">{b.guest_name}</TableCell>
                    <TableCell className="text-sm">{b.guest_count}</TableCell>
                    <TableCell className="text-xs">{b.check_in}</TableCell>
                    <TableCell className="text-xs">{b.check_out}</TableCell>
                    <TableCell className="text-sm">{b.nights}</TableCell>
                    <TableCell className="text-xs">
                      {b.platform ? (
                        <Badge variant="outline" className="text-[10px] px-1 py-0">
                          {PLATFORM_LABELS[b.platform] ?? b.platform}
                        </Badge>
                      ) : "—"}
                    </TableCell>
                    <TableCell><StatusBadge status={b.status || "pending"} /></TableCell>
                    <TableCell className="text-sm">{b.fee > 0 ? `$${b.fee}` : "—"}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        title="View booking details"
                        aria-label="View booking details"
                        onClick={() => openViewDialog(b)}
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {!isSearching && !isLoading && totalCount > 0 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-xs text-muted-foreground">
                Page {page + 1} of {totalPages} &middot; {totalCount} booking{totalCount !== 1 ? "s" : ""}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View / Manage Booking Dialog */}
      <Dialog open={!!viewBooking} onOpenChange={(v) => { if (!v) { setViewBooking(null); setBlockReason(""); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Booking Details</DialogTitle>
          </DialogHeader>
          {viewBooking && (
            <div className="space-y-4 py-1">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground">Unit</p>
                  <p className="font-medium">{viewBooking.unit?.unit_number || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Host</p>
                  <p className="font-medium">{viewBooking.host?.profile?.full_name || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Guest Name</p>
                  <p className="font-medium">{viewBooking.guest_name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Guest Email</p>
                  <p className="font-medium">{viewBooking.guest_email || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Guest Phone</p>
                  <p className="font-medium">{viewBooking.guest_phone || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Guests</p>
                  <p className="font-medium">{viewBooking.guest_count}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <StatusBadge status={viewBooking.status || "pending"} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Platform</p>
                  <p className="font-medium">{viewBooking.platform ? PLATFORM_LABELS[viewBooking.platform] ?? viewBooking.platform : "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Booking Ref #</p>
                  <p className="font-medium font-mono text-xs">{viewBooking.booking_reference || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Purpose</p>
                  <p className="font-medium">{viewBooking.purpose ? PURPOSE_LABELS[viewBooking.purpose] ?? viewBooking.purpose : "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Check-in</p>
                  <p className="font-medium">{viewBooking.check_in}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Check-out</p>
                  <p className="font-medium">{viewBooking.check_out}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Nights</p>
                  <p className="font-medium">{viewBooking.nights}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Fee</p>
                  <p className="font-medium">{viewBooking.fee > 0 ? `$${viewBooking.fee}` : "—"}</p>
                </div>
                {viewBooking.vehicle_info && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">Vehicle Info</p>
                    <p className="font-medium">{viewBooking.vehicle_info}</p>
                  </div>
                )}
                {viewBooking.booking_notes && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">Notes / Special Requests</p>
                    <p className="text-sm bg-muted rounded-md p-2">{viewBooking.booking_notes}</p>
                  </div>
                )}
              </div>

              {viewBooking.blocked_reason && (
                <div className="rounded-md bg-destructive/10 p-3">
                  <p className="text-xs font-medium text-destructive">Block Reason</p>
                  <p className="text-xs text-destructive/80 mt-1">{viewBooking.blocked_reason}</p>
                </div>
              )}

              {/* Actions for pending bookings */}
              {viewBooking.status === "pending" && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <p className="text-xs font-medium text-muted-foreground">Actions</p>
                    <div className="space-y-2">
                      <Label htmlFor="block_reason" className="text-xs">Block Reason (required to block)</Label>
                      <Textarea
                        id="block_reason"
                        placeholder="Explain why this booking is being blocked..."
                        rows={2}
                        value={blockReason}
                        onChange={(e) => setBlockReason(e.target.value)}
                        className="text-xs"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 bg-success hover:bg-success/90 text-success-foreground"
                        onClick={handleApprove}
                        disabled={updateStatus.isPending}
                      >
                        <CheckCircle2 className="mr-1 h-3 w-3" /> Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="flex-1"
                        onClick={handleBlock}
                        disabled={updateStatus.isPending || !blockReason.trim()}
                      >
                        <XCircle className="mr-1 h-3 w-3" /> Block
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {/* Mark complete for approved bookings */}
              {viewBooking.status === "approved" && (
                <>
                  <Separator />
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full"
                    onClick={handleComplete}
                    disabled={updateStatus.isPending}
                  >
                    Mark as Completed
                  </Button>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setViewBooking(null); setBlockReason(""); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Booking Dialog */}
      <Dialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); resetForm(); } else setOpen(true); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Booking</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">

            {/* ── Host selection ── */}
            <div className="space-y-2">
              <Label>Host *</Label>
              <Select value={form.host_id} onValueChange={handleHostChange}>
                <SelectTrigger><SelectValue placeholder="Select a host" /></SelectTrigger>
                <SelectContent>
                  {hosts.map((h: any) => (
                    <SelectItem key={h.id} value={h.id}>
                      {h.profile?.full_name || "Unknown"} — Unit {h.unit?.unit_number || "?"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* ── Guest core info ── */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="guest_name">Guest Name *</Label>
                <Input id="guest_name" placeholder="Full name" value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="guest_count">Number of Guests</Label>
                <Input id="guest_count" type="number" min="1" value={form.guest_count} onChange={(e) => setForm({ ...form, guest_count: e.target.value })} />
              </div>
            </div>

            {/* ── Dates ── */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="check_in">Check-in *</Label>
                <Input id="check_in" type="date" value={form.check_in} onChange={(e) => setForm({ ...form, check_in: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="check_out">Check-out *</Label>
                <Input id="check_out" type="date" value={form.check_out} onChange={(e) => setForm({ ...form, check_out: e.target.value })} />
              </div>
            </div>
            {form.check_in && form.check_out && (
              <p className="text-xs text-muted-foreground">
                {calcNights(form.check_in, form.check_out)} night(s)
              </p>
            )}

            {/* ── Fee & Platform ── */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fee">Strata Fee ($)</Label>
                <Input id="fee" type="number" min="0" step="0.01" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Booking Platform</Label>
                <Select value={form.platform || "none"} onValueChange={(v) => setForm({ ...form, platform: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Select platform" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    <SelectItem value="airbnb">Airbnb</SelectItem>
                    <SelectItem value="vrbo">VRBO</SelectItem>
                    <SelectItem value="booking_com">Booking.com</SelectItem>
                    <SelectItem value="expedia">Expedia</SelectItem>
                    <SelectItem value="direct">Direct / Host</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* ── Optional guest & booking info ── */}
            <Separator />
            <Collapsible open={showOptionalGuest} onOpenChange={setShowOptionalGuest}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full justify-between text-xs text-muted-foreground hover:text-foreground">
                  Additional Guest & Booking Info (optional)
                  {showOptionalGuest ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="guest_email" className="text-xs">Guest Email</Label>
                    <Input id="guest_email" type="email" placeholder="email@example.com" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="guest_phone" className="text-xs">Guest Phone</Label>
                    <Input id="guest_phone" type="tel" placeholder="+1 604-555-0100" value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} className="h-9" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="booking_reference" className="text-xs">Platform Confirmation #</Label>
                    <Input id="booking_reference" placeholder="e.g. HM9X234ABC" value={form.booking_reference} onChange={(e) => setForm({ ...form, booking_reference: e.target.value })} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Purpose of Visit</Label>
                    <Select value={form.purpose || "none"} onValueChange={(v) => setForm({ ...form, purpose: v === "none" ? "" : v })}>
                      <SelectTrigger className="h-9"><SelectValue placeholder="Select if known" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not specified</SelectItem>
                        <SelectItem value="leisure">Leisure / Tourism</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                        <SelectItem value="family">Family Visit</SelectItem>
                        <SelectItem value="relocation">Relocation / Temporary Stay</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vehicle_info" className="text-xs">
                    Vehicle Info <span className="text-muted-foreground">(optional — license plate for parking records)</span>
                  </Label>
                  <Input id="vehicle_info" placeholder="e.g. ABC 1234 — Silver Toyota" value={form.vehicle_info} onChange={(e) => setForm({ ...form, vehicle_info: e.target.value })} className="h-9" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="booking_notes" className="text-xs">Special Requests / Notes</Label>
                  <Textarea
                    id="booking_notes"
                    placeholder="Any special requests, accessibility needs, or notes..."
                    rows={2}
                    value={form.booking_notes}
                    onChange={(e) => setForm({ ...form, booking_notes: e.target.value })}
                    className="text-xs"
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); resetForm(); }}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={createBooking.isPending || !form.host_id || !form.guest_name || !form.check_in || !form.check_out || calcNights(form.check_in, form.check_out) <= 0}
            >
              {createBooking.isPending ? "Creating..." : "Create Booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
